import { action, observable, runInAction, makeObservable } from 'mobx'
import * as MobxRouter from 'mobx-react-router'
import parse from 'query-string'
import { IOrganization } from '../../Definitions'
import { Client, LoginDto, PasswordRuleSettings } from '../../generated_client'
import addDefaultUserFields from '../../utils/addDefaultUserFields'
import GlobalStore from '../GlobalStore'
import NotificationStore from '../NotificationStore'

export interface IPasswordPattern {
  regex?: RegExp
  failMessage?: string
}

class LoginStore {
  @observable
  public isLoading: boolean = false
  @observable
  public isLoadingForgotPassword: boolean = false
  @observable
  public isLoadingMagicLink: boolean = false
  @observable
  public isModalOpen: boolean = false
  @observable
  public passwordPattern: IPasswordPattern = {}
  @observable
  public isResetTokenValid: boolean = false
  @observable
  public isPasswordExpirationWarningModalOpen: boolean = false
  @observable
  public daysUntilPasswordExpires: number = 0
  @observable
  public magicLinkSessionId: string | null = null
  @observable
  public isPollingMagicLink: boolean = false

  private pollInterval: NodeJS.Timeout | null = null

  private routerStore: any
  private client: Client
  private globalStore: GlobalStore
  private notificationStore: NotificationStore

  constructor(
    globalStore: GlobalStore,
    notificationStore: NotificationStore,
    routerStore: MobxRouter.RouterStore,
    client: Client
  ) {
    this.globalStore = globalStore
    this.notificationStore = notificationStore
    this.routerStore = routerStore
    this.client = client
    makeObservable(this)
  }

  @action.bound
  public closeDialog() {
    this.isModalOpen = false
  }

  @action.bound
  public forgotPassword(email: string) {
    this.isLoadingForgotPassword = true
    this.notificationStore.sendNotification(
      '200',
      'Password reset email being sent to ' +
        email.replace(/(?<=.{3}).*(?=\@.*?)/, '***') +
        '. It may take a few minutes.',
      { variant: 'info' }
    )
    return this.client
      .apiLoginForgotPasswordPost(new LoginDto({ email }))
      .then(() => {
        this.notificationStore.sendNotification(
          '200',
          'Password reset email has been sent.',
          {
            variant: 'success',
          }
        )
        runInAction(() => {
          this.closeDialog()
        })
      })
      .catch(() => {})
      .finally(() => {
        runInAction(() => {
          this.isLoadingForgotPassword = false
        })
      })
  }

  @action.bound
  public async requestMagicLink(email: string) {
    this.isLoadingMagicLink = true
    this.notificationStore.sendNotification(
      '200',
      'Magic link being sent to ' +
        email.replace(/(?<=.{3}).*(?=\@.*?)/, '***') +
        '. It may take 30-60 seconds to arrive.',
      { variant: 'info' }
    )
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000'
    try {
      const response = await fetch(`${baseUrl}/api/Login/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })

      if (response.ok) {
        const data = await response.json()
        const sessionId = data.sessionId

        runInAction(() => {
          this.magicLinkSessionId = sessionId
        })

        this.notificationStore.sendNotification(
          `magic-link-success-${Date.now()}`,
          'Magic link sent to ' +
            email.replace(/(?<=.{3}).*(?=\@.*?)/, '***') +
            '! Waiting for you to click the link...',
          { variant: 'success' }
        )

        this.startPolling()

        return { success: true, sessionId }
      } else {
        let errorMessage = 'Error sending magic link. Please try again.'
        try {
          const errorData = await response.json()
          errorMessage =
            errorData.message ||
            errorData.error ||
            errorData.detail ||
            errorData.title ||
            JSON.stringify(errorData)
        } catch (parseError) {
          errorMessage = `Error ${response.status}: ${response.statusText}`
        }

        this.notificationStore.sendNotification(
          `magic-link-error-${Date.now()}`,
          errorMessage,
          { variant: 'error' }
        )
        return { success: false }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Network error occurred'
      this.notificationStore.sendNotification(
        `magic-link-error-${Date.now()}`,
        `Error sending magic link: ${errorMessage}`,
        { variant: 'error' }
      )
      return { success: false }
    } finally {
      runInAction(() => {
        this.isLoadingMagicLink = false
      })
    }
  }

  @action.bound
  public async checkMagicLinkSession(sessionId: string) {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000'
    try {
      const response = await fetch(
        `${baseUrl}/api/Login/check-magic-link-session?sessionId=${sessionId}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.authenticated && data.user) {
          const user = addDefaultUserFields(data.user)

          if (!user.organization?.id) {
            return { authenticated: false }
          }

          this.stopPolling()

          runInAction(() => {
            this.globalStore.setUser(user)
            this.globalStore.changeTheme(user.organization!.id)
            this.globalStore.setCurrentAppOrganization(
              user.organization as unknown as IOrganization
            )
          })

          this.notificationStore.sendNotification(
            `magic-link-authenticated-${Date.now()}`,
            'Successfully authenticated!',
            { variant: 'success' }
          )

          this.navigateAfterLogin()
          return { authenticated: true }
        }
      }
      return { authenticated: false }
    } catch (error) {
      console.error('Error checking magic link session:', error)
      return { authenticated: false }
    }
  }

  @action.bound
  public startPolling() {
    if (this.pollInterval || !this.magicLinkSessionId) {
      return
    }

    runInAction(() => {
      this.isPollingMagicLink = true
    })

    this.pollInterval = setInterval(async () => {
      if (this.magicLinkSessionId) {
        await this.checkMagicLinkSession(this.magicLinkSessionId)
      }
    }, 2000)

    setTimeout(
      () => {
        this.stopPolling()
        if (this.isPollingMagicLink) {
          this.notificationStore.sendNotification(
            `magic-link-timeout-${Date.now()}`,
            'Magic link request timed out. Please try again.',
            { variant: 'warning' }
          )
        }
      },
      5 * 60 * 1000
    )
  }

  @action.bound
  public stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    runInAction(() => {
      this.isPollingMagicLink = false
      this.magicLinkSessionId = null
    })
  }

  @action.bound
  public async verifyMagicLink(email: string, token: string, sessionId?: string) {
    const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000'
    const url = `${baseUrl}/api/Login/login-with-magic-link`

    try {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          magicLinkToken: token,
          magicLinkSessionId: sessionId,
        }),
      })

      if (response.ok) {
        const data = await response.json()
        const userData = data.user

        if (userData) {
          const user = addDefaultUserFields(userData)

          if (user.organization?.id) {
            runInAction(() => {
              this.globalStore.setUser(user)
              this.globalStore.changeTheme(user.organization!.id)
              this.globalStore.setCurrentAppOrganization(
                user.organization as unknown as IOrganization
              )
            })
          }
        }

        if (!sessionId) {
          this.navigateAfterLogin()
        }

        return { success: true, isCrossDevice: !!sessionId }
      } else {
        return { success: false, error: `Invalid or expired token (${response.status})` }
      }
    } catch (error) {
      return { success: false, error: 'Error verifying magic link' }
    }
  }

  @action.bound
  public login(userName: string, password: string) {
    runInAction(() => {
      this.isLoading = true
    })
    return this.client
      .apiLoginPost(new LoginDto({ userName, password }))
      .then((resp) => {
        const data = resp.result
        const user = addDefaultUserFields(data)
        runInAction(() => {
          this.globalStore.setUser(user)
          this.globalStore.changeTheme(user.organization!.id)
          this.globalStore.setCurrentAppOrganization(
            user.organization as unknown as IOrganization
          )
          if (data.showPasswordExpirationWarning) {
            this.daysUntilPasswordExpires = data.daysUntilPasswordExpires ?? 0
            this.isPasswordExpirationWarningModalOpen = true
          } else {
            this.navigateAfterLogin()
          }
        })
      })
      .catch(() => {})
      .finally(() => {
        runInAction(() => {
          this.isLoading = false
        })
      })
  }

  @action.bound
  public getTokenValidity(userName: string, resetToken: string) {
    this.isLoading = true
    this.client
      .apiLoginTokenValidityPost(userName, resetToken)
      .then((resp) => {
        runInAction(() => {
          this.isResetTokenValid = resp.result
        })
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false
        })
      })
  }

  public logout() {
    this.client
      .apiLoginDelete()
      .then(() => {
        this.globalStore.clearUserAndRouteToLogin()
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false
        })
      })
  }

  @action.bound
  public openDialog() {
    this.isModalOpen = true
  }

  @action.bound
  public resetPassword(userName: string, password: string, resetPasswordToken: string) {
    this.isLoading = true
    return this.client
      .apiLoginPut(new LoginDto({ userName, password, resetPasswordToken }))
      .then(() => {
        this.notificationStore.sendNotification(
          '200',
          'Your password has been reset. Redirecting you to the home page.',
          { variant: 'success' }
        )
        this.isResetTokenValid = false
        this.login(userName, password)
      })
      .catch(() => {
        throw new Error()
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false
        })
      })
  }

  @action.bound
  public getPasswordPattern() {
    if (!this.passwordPattern) {
      return this.client
        .apiPasswordRulesGet()
        .then((response) => {
          runInAction(() => {
            this.passwordPattern = this.getPasswordPatternForRules(response.result)
          })
        })
        .catch((error) => {
          console.error('Error fetching password rules:', error)
          return Promise.resolve()
        })
    }
    return Promise.resolve()
  }

  private getPasswordPatternForRules(passwordRuleSettings: PasswordRuleSettings) {
    const regexObject: IPasswordPattern = {
      failMessage: '',
      regex: new RegExp(''),
    }
    let passwordRegex = ''

    const regexCap = `(?=.*[A-Z])`
    const regexLow = `(?=.*[a-z])`
    const regexDig = `(?=.*\\d)`
    const regexSpc = `(?=.*[#@$%*&?!^~;:_,.\\(\\)\\+\\-\\=\\[\\]\\{\\}\\<\\>])`
    let failMessage = 'Password should follow the following rules:\n'

    const regexLength = `[A-Za-z\\d#@$%*&?!^~;:_,.\\(\\)\\+\\-\\=\\[\\]\\{\\}\\<\\>]{${passwordRuleSettings.requiredLength},}`
    failMessage += `\t- Contain at least ${passwordRuleSettings.requiredLength} characters\n`

    if (passwordRuleSettings.requireUppercase) {
      passwordRegex += regexCap
      failMessage += '\t- Contain at least 1 uppercase letter\n'
    }
    if (passwordRuleSettings.requireLowercase) {
      passwordRegex += regexLow
      failMessage += '\t- Contain at least 1 lowercase letter\n'
    }
    if (passwordRuleSettings.requireDigit) {
      passwordRegex += regexDig
      failMessage += '\t- Contain at least 1 digit\n'
    }
    if (passwordRuleSettings.requireNonAlphanumeric) {
      passwordRegex += regexSpc
      failMessage += '\t- Contain at least 1 special character ($!@ etc.)'
    }

    regexObject.regex = new RegExp((passwordRegex += regexLength))
    regexObject.failMessage = failMessage

    return regexObject
  }

  private navigateAfterLogin() {
    let route = '/patients'
    const queryParams = parse.parse(this.routerStore.location.search)
    if (queryParams && queryParams.previousRoute) {
      route = atob('' + queryParams.previousRoute)
    }
    this.routerStore.push(route)
  }

  @action.bound
  public closePasswordExpirationWarningModal(isContinue: boolean) {
    this.isPasswordExpirationWarningModalOpen = false
    if (isContinue) {
      this.navigateAfterLogin()
    }
  }

  @action.bound
  public switchOrganization(organization: IOrganization) {
    if (!this.globalStore.isWorkflowAdmin) {
      this.notificationStore.sendNotification(
        '403',
        'Only workflow administrators can switch organizations',
        { variant: 'error' }
      )
      return
    }

    if (!organization) {
      this.notificationStore.sendNotification('404', 'Organization not found', {
        variant: 'error',
      })
      return
    }

    runInAction(() => {
      this.globalStore.setCurrentAppOrganization(organization)
      this.globalStore.changeTheme(organization.id!)
    })

    this.notificationStore.sendNotification('200', `Switched to ${organization.name}`, {
      variant: 'success',
    })

    this.routerStore.push('/')
  }
}

export default LoginStore
